import { useState, useEffect, useDeferredValue } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';
import { Spinner } from './ui';
import { useSymbolSearch } from '../api/hooks';
import { isManualPickerSymbolSelectable } from '../pages/user/manual-trading/utils/manualSymbolCompatibility';
import { normalizeSymbolSearchScope } from './symbolSearchScope';

// Trivial styled input (mirrors StrategyBuilder's local FInput) so this shared
// component depends only on shared modules.
function FInput({ className, ...props }) {
  return <input className={cn('input-base', className)} {...props} />;
}

const SYMBOL_SEGMENT_CHIPS = [
  { value: 'all',       label: 'All'       },
  { value: 'stock',     label: 'Stock'     },
  { value: 'commodity', label: 'Commodity' },
  { value: 'fno',       label: 'F&O'       },
  { value: 'etf',       label: 'ETF'       },
  { value: 'index',     label: 'Index'     },
];

const SYMBOL_EXCHANGE_CHIPS = [
  { value: 'NSE', label: 'NSE' },
  { value: 'BSE', label: 'BSE' },
  { value: 'MCX', label: 'MCX' },
  { value: 'all', label: 'All' },
];

// Shared Symbol Search modal (API-backed via useSymbolSearch / GET /symbols/search).
//
// Optional, backward-compatible lock props — when set, the segment/exchange filter
// chips are hidden and the search is restricted to that scope (used by Manual Trading,
// where the strategy fixes exchange + segment). Absent props → original behavior.
//   lockedExchange : 'NSE' | 'BSE' | 'MCX'
//   lockedSegment  : 'INDEX' | 'STOCK' | ... (case-insensitive)
//   scopeHint      : e.g. 'NSE · Index' (shown in the header when locked)
//   excludeDirectOptionContracts : Manual OPTION watchlists only; leaves every
//                                  other SymbolPicker consumer unchanged.
//   manualTradingConfig : optional Manual-only config used to filter selectable
//                         rows without changing the global search response.
export default function SymbolPickerModal({
  open,
  selectedSymbol,
  onClose,
  onSelect,
  lockedExchange,
  lockedSegment,
  scopeHint,
  excludeDirectOptionContracts = false,
  manualTradingConfig,
}) {
  const lockedSeg = lockedSegment ? String(lockedSegment).toLowerCase() : null;
  const isLocked = !!(lockedExchange || lockedSegment);

  const [query, setQuery]       = useState('');
  const [segment, setSegment]   = useState(lockedSeg || 'all');
  const [exchange, setExchange] = useState(lockedExchange || 'NSE');
  const deferredQuery = useDeferredValue(query);
  const searchScope = normalizeSymbolSearchScope(segment, exchange);
  const searchQ = useSymbolSearch(deferredQuery, searchScope.segment, searchScope.exchange, open);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSegment(lockedSeg || 'all');
    setExchange(lockedExchange || 'NSE');
  }, [open, lockedSeg, lockedExchange]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  const rows = (searchQ.data || []).filter((row) => {
    if (manualTradingConfig) return isManualPickerSymbolSelectable(manualTradingConfig, row);
    if (!excludeDirectOptionContracts) return true;
    const body = String(row?.symbol || '').trim().toUpperCase().split(':').pop() || '';
    return !( /\d/.test(body) && (body.endsWith('CE') || body.endsWith('PE')) );
  });
  const modal = (
    <div className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-0 flex items-start justify-center p-4 pt-[8vh]">
        <div
          className="relative bg-bg-panel border border-bg-border rounded-xl w-full max-w-xl shadow-modal flex flex-col animate-slide-up"
          style={{ maxHeight: 'min(600px, 85vh)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-bg-border/60 shrink-0">
            <div>
              <h3 className="text-sm font-semibold text-txt-primary">Symbol Search</h3>
              <p className="text-2xs font-mono text-txt-muted mt-0.5">
                {isLocked
                  ? <>Scope: <span className="text-txt-secondary">{scopeHint || `${exchange} · ${lockedSeg || 'all'}`}</span></>
                  : 'Pick a canonical symbol for the strategy'}
              </p>
            </div>
            <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={onClose}>✕</button>
          </div>

          {/* Search input */}
          <div className="px-4 pt-3 shrink-0">
            <FInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search ticker, name, description…"
              autoFocus
              className="h-10 text-sm"
            />
          </div>

          {/* Filters — hidden when scope is locked */}
          {!isLocked && (
            <div className="px-4 pt-2.5 space-y-1.5 shrink-0">
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest mr-1">Segment</span>
                {SYMBOL_SEGMENT_CHIPS.map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    onClick={() => {
                      setSegment(chip.value);
                      if (chip.value === 'commodity') setExchange('MCX');
                    }}
                    className={cn(
                      'px-2.5 py-0.5 rounded text-2xs font-mono border transition-colors duration-100',
                      segment === chip.value
                        ? 'border-amber/50 bg-amber/10 text-amber'
                        : 'border-bg-border text-txt-secondary hover:border-amber/25 hover:text-txt-primary'
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              {segment === 'commodity' ? (
                <div className="flex flex-wrap gap-1.5 items-center">
                  <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest mr-1">Exchange</span>
                  <span className="px-2.5 py-0.5 rounded text-2xs font-mono border border-info/50 bg-info/10 text-info">
                    MCX
                  </span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 items-center">
                  <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest mr-1">Exchange</span>
                  {SYMBOL_EXCHANGE_CHIPS.map((chip) => (
                    <button
                      key={chip.value}
                      type="button"
                      onClick={() => setExchange(chip.value)}
                      className={cn(
                        'px-2.5 py-0.5 rounded text-2xs font-mono border transition-colors duration-100',
                        exchange === chip.value
                          ? 'border-info/50 bg-info/10 text-info'
                          : 'border-bg-border text-txt-secondary hover:border-info/25 hover:text-txt-primary'
                      )}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Results */}
          <div className="mt-2.5 mx-4 mb-4 rounded-lg border border-bg-border bg-bg-surface/20 overflow-y-auto flex-1 min-h-[180px]">
            {searchQ.isLoading ? (
              <div className="py-10 flex justify-center items-center gap-2 text-txt-muted">
                <Spinner />
                <span className="text-2xs font-mono">Searching…</span>
              </div>
            ) : searchQ.isError ? (
              <div className="px-4 py-8 text-2xs font-mono text-bear text-center">
                Failed to load symbols. Please retry.
              </div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-8 text-2xs font-mono text-txt-muted text-center">
                {query ? 'No symbols match your search.' : 'Type to search symbols.'}
              </div>
            ) : (
              <div className="divide-y divide-bg-border/50">
                {rows.map((row) => {
                  const active = selectedSymbol === row.symbol;
                  return (
                    <button
                      key={`${row.symbol}-${row.segment}`}
                      type="button"
                      onClick={() => onSelect?.(row)}
                      className={cn(
                        'w-full text-left px-3 py-2.5 transition-colors duration-100 flex items-center gap-3',
                        'hover:bg-bg-hover',
                        active && 'bg-amber/10 border-l-2 border-l-amber pl-2.5'
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-txt-primary font-mono">{row.display_name}</span>
                          <span className={cn(
                            'text-2xs font-mono px-1.5 py-0.5 rounded border shrink-0',
                            row.segment === 'fno'   ? 'border-warn/30 bg-warn/10 text-warn' :
                            row.segment === 'index' ? 'border-info/30 bg-info/10 text-info' :
                            'border-bg-border text-txt-muted'
                          )}>
                            {row.segment?.toUpperCase()}
                          </span>
                        </div>
                        {row.description && (
                          <div className="text-2xs text-txt-secondary mt-0.5 truncate">{row.description}</div>
                        )}
                        <div className="text-[10px] font-mono text-txt-muted mt-0.5 truncate">{row.symbol}</div>
                      </div>
                      {active && <span className="text-amber text-xs shrink-0">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}
